import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { ShieldCheck, Maximize2, Droplets, Truck, Wrench, BadgeCheck } from "lucide-react";

const featuredModels = [
  {
    name: "ARIA H60",
    price: "Offerta speciale: 600 € per unità",
    path: "/products/aria",
    angle: "Sospeso a parete",
    text: "Sospeso, compatto, ideale per bagni italiani di 4–6 m² e per recuperare lo spazio del bidet separato. Certificato CE e WaterSense.",
  },
  {
    name: "REM7",
    price: "Offerta speciale: 600 € per unità",
    path: "/products/rem7",
    angle: "Modello di riferimento",
    text: "Lo standard di progetto: bidet integrato, riscaldamento istantaneo senza serbatoio e sterilizzazione UV-C dell'ugello.",
  },
  {
    name: "VENA M80F",
    price: "Offerta speciale: 600 € per unità",
    path: "/products/vena",
    angle: "Sostituzione del bidet",
    text: "Pensato per chi vuole eliminare il bidet separato mantenendo tutte le funzioni di igiene intima.",
  },
  {
    name: "LUXA M80",
    price: "Offerta speciale: 600 € per unità",
    path: "/products/luxa",
    angle: "Suite e attici",
    text: "Finiture premium, apertura a sensore, luce notturna LED e presets utente per residenze e hotel di alta gamma.",
  },
];

const Italy = () => {
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "European Markets", url: "/european-markets" },
    { name: "Italy", url: "/markets/italy" }
  ];

  return (
    <>
      <SEO
        title="WC Intelligenti Italia: Igiene UV-C e Salvaspazio"
        description="WC intelligenti REM7 per l'Italia: sterilizzazione UV-C, bidet integrato al posto del bidet separato e modelli sospesi ARIA. CE certificato."
        keywords="WC intelligenti Italia, sanitari intelligenti, toilette intelligente Italia, bagno intelligente, bidet integrato, WC sospeso intelligente, igiene bagno UV"
        url="/markets/italy"
        breadcrumbs={breadcrumbs}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "WC Intelligenti Italia",
          "description": "Sistemi WC intelligenti premium per il mercato italiano: igiene UV-C, bidet integrato e soluzioni salvaspazio",
          "url": "https://www.aureum7trading.com/en/markets/italy"
        }}
      />
      <Navigation />

      <main className="min-h-screen bg-gradient-to-b from-background to-background/95">
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                  <span className="text-2xl">🇮🇹</span>
                  <span className="text-sm font-medium">Mercato Italiano</span>
                </div>

                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  WC Intelligenti Italia
                </h1>

                <p className="text-xl text-muted-foreground mb-8">
                  Igiene superiore con sterilizzazione UV-C e un bidet integrato che libera
                  lo spazio del sanitario separato. L'Italia è il primo mercato europeo per
                  i WC intelligenti — e il motivo non è la domotica, ma l'igiene e lo spazio.
                </p>

                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <LocalizedLink to="/contact">Richiedi Preventivo</LocalizedLink>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <LocalizedLink to="/products/aria">Scopri ARIA Sospeso</LocalizedLink>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Card className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <ShieldCheck className="w-8 h-8 text-primary" />
                    <h3 className="text-2xl font-bold">Igiene &amp; Spazio</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Droplets className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Sterilizzazione UV-C</p>
                        <p className="text-sm text-muted-foreground">Ugello sterilizzato tra un utilizzo e l'altro, con autopulizia</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Maximize2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Un sanitario al posto di due</p>
                        <p className="text-sm text-muted-foreground">Il bidet integrato recupera fino a un intero modulo di pavimento</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <BadgeCheck className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold">Conformità CE</p>
                        <p className="text-sm text-muted-foreground">Prodotti certificati CE e WaterSense, spina europea 230V</p>
                      </div>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Three pillars */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Perché gli Italiani Stanno Passando al WC Intelligente
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Droplets className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Igiene Prima di Tutto</h3>
                <p className="text-muted-foreground">
                  L'acqua ha sempre avuto un ruolo centrale nell'igiene intima italiana.
                  Il getto regolabile in pressione e temperatura, l'ugello autopulente e la
                  sterilizzazione UV-C portano quella abitudine a uno standard superiore.
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Maximize2 className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Recupero di Spazio</h3>
                <p className="text-muted-foreground">
                  Nei bagni degli appartamenti storici e delle nuove costruzioni compatte,
                  eliminare il bidet separato libera spazio reale: più profondità di
                  passaggio, un mobile in più, una doccia più larga.
                </p>
              </Card>

              <Card className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <Wrench className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Ristrutturazioni</h3>
                <p className="text-muted-foreground">
                  La ristrutturazione del bagno è il momento naturale per il passaggio:
                  l'impianto è già aperto e l'aggiunta di un punto elettrico protetto
                  costa molto meno che in un secondo intervento.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Compact / wall-hung */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-6">
              Quali Modelli per Bagni Compatti e Sospesi
            </h2>
            <p className="text-center text-muted-foreground mb-12">
              Non tutti i modelli sono adatti a una parete attrezzata esistente. Ecco come
              scegliere.
            </p>

            <div className="space-y-8">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Bagni fino a 6 m² — ARIA H60 sospeso</h3>
                <p className="text-muted-foreground">
                  ARIA è il modello sospeso della gamma: si monta su telaio a incasso, lascia
                  il pavimento libero e semplifica la pulizia. È la scelta corretta quando il
                  bidet separato viene rimosso e lo spazio va recuperato visivamente.{" "}
                  <LocalizedLink to="/products/aria" className="text-primary underline underline-offset-4">
                    Vedi ARIA H60
                  </LocalizedLink>
                  .
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Sostituzione diretta a pavimento</h3>
                <p className="text-muted-foreground">
                  Dove il telaio a incasso non esiste, REM7 e VENA si installano al posto del
                  sanitario esistente mantenendo lo scarico attuale, con un intervento
                  nettamente più contenuto rispetto alla conversione a sospeso.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-xl font-semibold mb-3">Verifica prima dell'ordine</h3>
                <p className="text-muted-foreground">
                  Tre controlli: interasse e posizione dello scarico, pressione di rete
                  sufficiente per il riscaldamento istantaneo, e un punto elettrico 230V
                  dedicato e protetto da differenziale fuori dalla zona bagnata.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Featured models */}
        <section className="py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Modelli in Evidenza per l'Italia
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {featuredModels.map((m) => (
                <Card key={m.name} className="p-6 flex flex-col">
                  <div className="flex items-baseline justify-between gap-4 mb-2">
                    <h3 className="text-xl font-semibold">{m.name}</h3>
                    <span className="text-primary font-semibold whitespace-nowrap">da {m.price}</span>
                  </div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{m.angle}</p>
                  <p className="text-muted-foreground flex-1">{m.text}</p>
                  <Button asChild variant="outline" className="mt-6 self-start">
                    <LocalizedLink to={m.path}>Dettagli</LocalizedLink>
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance, shipping, installation */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12">
              Conformità, Spedizione e Installazione in Italia
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6">
                <BadgeCheck className="w-6 h-6 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Conformità UE</h3>
                <p className="text-muted-foreground text-sm">
                  Certificazione CE e WaterSense, alimentazione 230V con spina europea,
                  documentazione tecnica disponibile per il progettista e per la pratica edilizia.
                </p>
              </Card>
              <Card className="p-6">
                <Truck className="w-6 h-6 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Spedizione</h3>
                <p className="text-muted-foreground text-sm">
                  Trasporto aereo in 10–15 giorni sui codici a stock per cantieri a fasi;
                  trasporto marittimo in circa 40 giorni, con circa 240 unità per container
                  da 40 piedi per le forniture complete.
                </p>
              </Card>
              <Card className="p-6">
                <Wrench className="w-6 h-6 text-primary mb-4" />
                <h3 className="text-lg font-semibold mb-2">Installazione</h3>
                <p className="text-muted-foreground text-sm">
                  Supporto tecnico all'idraulico e all'elettricista, schede di installazione
                  e assistenza post-vendita per progetti residenziali e alberghieri.
                </p>
              </Card>
            </div>

            <div className="mt-12 text-center text-muted-foreground">
              <p>
                Per il resto della fornitura bagno — vasche freestanding, idromassaggi e
                mobili lavabo — consultate la{" "}
                <LocalizedLink to="/bath-sanitary" className="text-primary underline underline-offset-4">
                  collezione Bath &amp; Sanitary
                </LocalizedLink>
                , oppure tornate alla{" "}
                <LocalizedLink to="/european-markets" className="text-primary underline underline-offset-4">
                  panoramica dei mercati europei
                </LocalizedLink>
                .
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Igiene UV-C e Bagni più Ampi, in Tutta Italia
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contatta il nostro team per una consulenza tecnica e un preventivo per il tuo progetto
            </p>
            <Button asChild size="lg">
              <LocalizedLink to="/contact">Contattaci Ora</LocalizedLink>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Italy;
